import React, { FC, useEffect, useRef, useState } from "react";
import { dataSubArray } from "./Drawer.fixtures";
import { useLocation } from "react-router-dom";
import { Disclosure } from "@headlessui/react";
import { useAppDispatch } from "store/hooks";
import { refreshFlowBuilder } from "reducers/flow-builder.reducer";
import laudspeakerIcon from "../../assets/images/leadstats-logo.svg";
import leadstatsLogo from "../../assets/images/leadstats-logo-with-text.svg";
import useHover from "hooks/useHover";
import { Link } from "react-router-dom";
import MessagesUnreadBadge from "components/MessagesUnreadBadge/MessagesUnreadBadge";
import useLeadStatsFeatureFlags, {
  LeadStatsFeatureFlags,
} from "hooks/useLeadStatsFeatureFlags";

interface NavigationItem {
  id: string;
  imgIcon: string;
  text: string;
  type: string;
  link?: string;
  children?: NavigationItem[];
}

export interface ResponsiveDrawerProps {
  expandable?: boolean;
}

function filterNavigation(
  items: NavigationItem[],
  flags: LeadStatsFeatureFlags
): NavigationItem[] {
  const showAutomationQueue =
    flags.automation.enabled || flags.bulkRuns.enabled;

  return items
    .map((item) => {
      if (!item.children) return item;
      return {
        ...item,
        children: item.children.filter(
          (child) => child.id !== "automation-queue" || showAutomationQueue
        ),
      };
    })
    .filter((item) => !item.children || item.children.length > 0);
}

const ResponsiveDrawer: FC<ResponsiveDrawerProps> = ({ expandable }) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { flags } = useLeadStatsFeatureFlags();
  const navigation = React.useMemo(
    () => filterNavigation(dataSubArray, flags),
    [flags]
  );

  const drawerRef = useRef<HTMLDivElement>(null);

  const isHovered = useHover(drawerRef);

  const [isUserCollapsed, setIsUserCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("sidebar-collapsed") === "true";
  });

  useEffect(() => {
    window.localStorage.setItem("sidebar-collapsed", String(isUserCollapsed));
  }, [isUserCollapsed]);

  const isExpanded = isUserCollapsed ? false : expandable ? isHovered : true;
  const isRouteActive = (link?: string) =>
    !!link &&
    ((link === "/home" && location.pathname === "/") ||
      location.pathname === link ||
      location.pathname.startsWith(`${link}/`));

  useEffect(() => {
    dispatch(refreshFlowBuilder());
  }, [location.pathname]);

  return (
    <div
      className={`${
        expandable
          ? "absolute w-[58px] hover:!w-[236px] transition-[width] [&_.notexapndable]:hover:!scale-100"
          : isUserCollapsed
          ? "relative min-w-[58px] max-w-[58px] transition-[width]"
          : "relative min-w-[236px] max-w-[236px] transition-[width]"
      } top-0 left-0 flex h-screen flex-col overflow-hidden text-[13px] leading-[20px] font-medium font-manrope text-themed-text z-[100] bg-themed-sidebar border-r border-themed-border`}
      ref={drawerRef}
    >
      <div className="flex h-full flex-col">
        <div className="flex h-[60px] min-h-[60px] w-full items-center gap-2 border-b border-themed-border px-4">
          <img
            className={`${
              expandable
                ? "notexapndable scale-0 transition-[width] delay-1000"
                : ""
            } h-[24px] max-w-[154px] object-contain ${
              !isExpanded ? "hidden" : ""
            }`}
            src={leadstatsLogo}
            alt="Leadstats"
          />
          {!isExpanded &&
            (expandable ? (
              <img
                className="mx-auto h-[26px] w-[26px] object-contain"
                src={laudspeakerIcon}
                alt="Leadstats"
              />
            ) : (
              <button
                type="button"
                onClick={() => setIsUserCollapsed(false)}
                aria-label="Expand sidebar"
                className="mx-auto flex h-[32px] w-[32px] items-center justify-center rounded-lg transition-colors hover:bg-themed-elevated"
              >
                <img
                  className="h-[26px] w-[26px] object-contain"
                  src={laudspeakerIcon}
                  alt=""
                />
              </button>
            ))}
          {!expandable && isExpanded && (
            <button
              type="button"
              onClick={() => setIsUserCollapsed(true)}
              aria-label="Collapse sidebar"
              className="ml-auto flex h-[24px] w-[24px] items-center justify-center rounded-md border border-themed-border bg-transparent text-themed-textSecondary transition-colors hover:bg-themed-elevated hover:text-themed-text"
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="transition-transform"
              >
                <path
                  d="M7.5 9L4.5 6L7.5 3"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
        </div>
        <nav className="flex min-h-0 w-full flex-1 flex-col gap-[2px] overflow-y-auto px-[10px] py-[14px]">
          {(!expandable || isExpanded) && (
            <div className="select-none px-2 pb-2 pt-1 text-[10px] font-bold uppercase tracking-[0.13em] text-themed-textSecondary opacity-70">
              Workspace
            </div>
          )}
          {navigation.map((navigationItem) => (
            <div
              className="w-full flex justify-center items-center"
              id={navigationItem.id}
              key={navigationItem.id}
            >
              {navigationItem.children ? (
                <div className="w-full">
                  <Disclosure
                    defaultOpen={navigationItem.children.some((child) =>
                      isRouteActive(child.link)
                    )}
                  >
                    {({ open }) => (
                      <div className="flex flex-col gap-[2px]">
                        <Disclosure.Button className="w-full rounded-lg">
                          <div
                            className={`relative flex h-[36px] w-full cursor-pointer select-none items-center gap-1 rounded-lg px-1 transition-colors ${
                              navigationItem.children?.some((child) =>
                                isRouteActive(child.link)
                              )
                                ? "bg-themed-selected text-themed-text"
                                : "text-themed-textSecondary hover:bg-themed-hover hover:text-themed-text"
                            }`}
                          >
                            <div className="flex h-[28px] w-[28px] min-w-[28px] items-center justify-center rounded-md">
                              <img
                                className="drawer-icon h-[17px] w-[17px] object-contain opacity-80"
                                src={navigationItem.imgIcon}
                                alt=""
                              />
                            </div>
                            <span
                              className={`${
                                expandable
                                  ? "notexapndable scale-0 transition-[width] delay-150"
                                  : ""
                              } whitespace-nowrap ${!isExpanded && "hidden"}`}
                            >
                              {navigationItem.text}
                            </span>
                            <div
                              className={`${
                                expandable
                                  ? "notexapndable scale-0 transition-[width] delay-150 absolute top-1/2 -translate-y-1/2 right-[5px]"
                                  : "ml-auto"
                              } ${open ? "" : "rotate-180"} ${
                                expandable && isExpanded && "!delay-0"
                              } ${!isExpanded ? "hidden" : ""}`}
                            >
                              <svg
                                width="10"
                                height="10"
                                viewBox="0 0 10 10"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  fillRule="evenodd"
                                  clipRule="evenodd"
                                  d="M7.58573 6.94449C7.97626 7.33501 8.53028 7.41416 8.82317 7.12127C9.11606 6.82837 9.03692 6.27435 8.64639 5.88383L5.81797 3.0554C5.57464 2.81208 5.26784 2.68964 5.00029 2.70275C4.73263 2.68949 4.42564 2.81193 4.18218 3.05538L1.35376 5.88381C0.963233 6.27433 0.884088 6.82835 1.17698 7.12125C1.46987 7.41414 2.02389 7.33499 2.41442 6.94447L5.00007 4.35882L7.58573 6.94449Z"
                                  fill="currentColor"
                                />
                              </svg>
                            </div>
                          </div>
                        </Disclosure.Button>
                        <Disclosure.Panel>
                          <div
                            className={`flex flex-col gap-[2px] ${
                              !expandable || isExpanded
                                ? "ml-[18px] border-l border-themed-border pl-[6px]"
                                : ""
                            }`}
                          >
                            {(navigationItem.children ?? []).map((child) => (
                              <Link
                                className={`flex h-[34px] w-full cursor-pointer select-none items-center rounded-lg ${
                                  !expandable || isExpanded
                                    ? ""
                                    : "justify-center"
                                }`}
                                id={child.id}
                                to={child.link ?? "/"}
                                key={child.id}
                              >
                                <div
                                  className={`flex h-full w-full items-center gap-1 rounded-lg px-1 transition-colors ${
                                    isRouteActive(child.link)
                                      ? "bg-themed-selected font-semibold text-themed-text"
                                      : "text-themed-textSecondary hover:bg-themed-hover hover:text-themed-text"
                                  }`}
                                >
                                  <div
                                    className={`flex h-[26px] w-[26px] min-w-[26px] items-center justify-center rounded-md ${
                                      !isExpanded && isRouteActive(child.link)
                                        ? "bg-themed-accentSoft"
                                        : ""
                                    }`}
                                  >
                                    <img
                                      className="drawer-icon h-[15px] w-[15px] object-contain opacity-80"
                                      src={child.imgIcon}
                                      alt=""
                                    />
                                  </div>
                                  <span
                                    className={`${
                                      expandable
                                        ? "notexapndable scale-0 transition-[width] delay-1000"
                                        : ""
                                    } whitespace-nowrap inline-block overflow-hidden ${
                                      !isExpanded && "hidden"
                                    }`}
                                  >
                                    {child.text}
                                  </span>
                                </div>
                              </Link>
                            ))}
                          </div>
                        </Disclosure.Panel>
                      </div>
                    )}
                  </Disclosure>
                </div>
              ) : (
                <Link
                  className={`flex h-[36px] w-full cursor-pointer select-none items-center rounded-lg ${
                    expandable && isExpanded ? "" : "justify-center"
                  }`}
                  to={navigationItem.link ?? "/"}
                  id={navigationItem.id}
                >
                  <div
                    className={`flex h-full w-full items-center gap-1 rounded-lg px-1 pr-2 transition-colors ${
                      isRouteActive(navigationItem.link)
                        ? "bg-themed-selected font-semibold text-themed-text"
                        : "text-themed-textSecondary hover:bg-themed-hover hover:text-themed-text"
                    }`}
                  >
                    <div
                      className={`flex h-[28px] w-[28px] min-w-[28px] items-center justify-center rounded-md ${
                        !isExpanded && isRouteActive(navigationItem.link)
                          ? "bg-themed-accentSoft"
                          : ""
                      }`}
                    >
                      <img
                        className="drawer-icon h-[17px] w-[17px] object-contain opacity-80"
                        src={navigationItem.imgIcon}
                        alt=""
                      />
                    </div>
                    <span
                      className={`${
                        expandable
                          ? "notexapndable scale-0 transition-[width] delay-1000"
                          : ""
                      } whitespace-nowrap ${!isExpanded && "hidden"}`}
                    >
                      {navigationItem.text}
                    </span>
                    {navigationItem.id === "messages" && isExpanded && (
                      <MessagesUnreadBadge />
                    )}
                  </div>
                </Link>
              )}
            </div>
          ))}
        </nav>
      </div>
    </div>
  );
};
export default ResponsiveDrawer;
